/**
 * A consent request that waits while the person signs in.
 *
 * The consent page (/auth/oauth-consent) is reached from an AI agent's sign-in;
 * if nobody is logged in, it remembers the request here and sends the person to
 * the login. AuthTransitionService then goes back to the consent page instead
 * of the dashboard. Session storage only — the request lives 10 minutes.
 */

const KEY = 'al-oauth-consent';
const MAX_AGE_MS = 10 * 60 * 1000;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isConsentRequestId(value: string | null | undefined): value is string {
  return !!value && UUID.test(value);
}

export function rememberPendingConsent(requestId: string): void {
  try {
    sessionStorage.setItem(KEY, JSON.stringify({ id: requestId, at: Date.now() }));
  } catch {
    // storage blocked: after the login the person lands on the dashboard and starts again
  }
}

/** The consent page to go back to after the login (once), or null. */
export function takePendingConsent(): string | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    sessionStorage.removeItem(KEY);
    if (!raw) return null;
    const { id, at } = JSON.parse(raw) as { id?: string; at?: number };
    if (!isConsentRequestId(id) || typeof at !== 'number' || Date.now() - at > MAX_AGE_MS) return null;
    return `/auth/oauth-consent?request=${id}`;
  } catch {
    return null;
  }
}
